import { assessmentQuestionnaireApiHandler } from '@api/assessmentQuestionnaireApiHandler';
import LightBulbIcon from '@assets/images/icons/lightbulb-outlined.svg';
import { smoothScrollTo } from '@helpers/general.helpers';
import { useAppSelector } from '@hooks/useAppSelector';
import { MotionImage, MotionText, SanitizedText, showToast } from '@shared/atoms';
import {
  CurrentPrioritiesCard,
  LoadingBackdrop,
  RecommendedAreasCard,
  SystemButton,
} from '@shared/molecules';
import { useIsFetching, useMutation } from '@tanstack/react-query';
import { ToastMessages } from 'constants/toastMessages';
import { TALK_TO_ADVISOR_AREAS_DISCUSS_MESSAGE } from 'custom.d';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RootState } from 'store';
import { ConvertedFocusArea } from 'store/Slices/QuestionnaireSliceV2';
import styles from './index.module.scss';

export const TalkToAdviserAreasSelection: React.FunctionComponent<
  TalkToAdviserAreasSelectionProps
> = (props) => {
  const { handleSkip } = props;
  const companyConfig = useAppSelector((state: RootState) => state.companyConfig.data);
  const [step, handleStep] = useState<
    'talkToAdvisorMessage' | 'talkToAdvisorDiscussion' | 'talkToAdvisorCompletion'
  >('talkToAdvisorMessage');

  const [selectedAreaIds, setSelectedAreaIds] = useState<number[]>([]);
  const [noSelection, setNoSelection] = useState(true);
  const user = localStorage.getItem('user');
  const { chosenQuestionnaire, createdAssessmentDetails, focusAreas } = useAppSelector(
    (state) => state.questionnaireV2 || {},
  );
  const [loadingAreas, setLoadingAreas] = useState(true);

  const stepContentRef = useRef<HTMLDivElement>(null);
  const isFetching = useIsFetching({ queryKey: ['active-focus-areas'] });
  const isPendingActiveAreas = isFetching > 0;

  useEffect(() => {
    // When step changes, move focus to the content
    if (stepContentRef.current) {
      stepContentRef.current.focus();
    }
  }, [step]);

  useEffect(() => {
    if (isPendingActiveAreas) {
      setLoadingAreas(true);
    } else {
      setTimeout(() => {
        setLoadingAreas(false);
      }, 1000);
    }
  }, [isPendingActiveAreas]);

  const useSubmitTalkToAdvisor = () =>
    useMutation({
      mutationFn: ({
        assessmentId,
        focusAreaIds,
      }: {
        assessmentId: number;
        focusAreaIds: number[];
      }) => {
        const payload = {
          assessmentId,
          focusAreaIds,
        };

        return assessmentQuestionnaireApiHandler.createTalkToAdvisorRequest(payload);
      },
    });

  const submitTalkToAdvisorMutation = useSubmitTalkToAdvisor();

  const handleSelectFocus = (id: number) => {
    setSelectedAreaIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        const newSelectedIds = prevSelectedIds.filter((selectedId) => selectedId !== id);
        setNoSelection(newSelectedIds.length === 0);

        return newSelectedIds;
      }

      if (prevSelectedIds.length < 11) {
        const newSelectedIds = [...prevSelectedIds, id];
        setNoSelection(false);

        return newSelectedIds;
      }

      return prevSelectedIds;
    });
  };

  const handleRemovePriority = (id: number) => {
    setSelectedAreaIds((prevSelectedIds) => {
      const newSelectedIds = prevSelectedIds.filter((selectedId) => selectedId !== id);
      setNoSelection(newSelectedIds.length === 0);

      return newSelectedIds;
    });
  };

  const handleSubmitAdvisor = () => {
    if (createdAssessmentDetails) {
      const payload = {
        assessmentId: createdAssessmentDetails?.id,
        focusAreaIds: selectedAreaIds,
      };
      submitTalkToAdvisorMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response?.success) {
            handleStep('talkToAdvisorCompletion');
            const ariaLiveEl = document.getElementById('advisor-selection-status');
            if (ariaLiveEl) {
              ariaLiveEl.textContent = 'Your request has been sent successfully.';
            }

            showToast(
              ToastMessages.talkToAdvisor.success.SUCCESSFULLY_SHARED,
              undefined,
              'success',
            );
          } else {
            showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
          }
        },
        onError: (error) => {
          showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
        },
      });
    } else {
      showToast(ToastMessages.talkToAdvisor.error.QUESTIONNAIRE_NOT_SELECTED, undefined, 'error');
    }
  };

  const activePriorities = useMemo(() => {
    if (!focusAreas) return [];

    return focusAreas.slice(0, 20) as ConvertedFocusArea[];
  }, [focusAreas]);

  return (
    <div className={styles.tasContainer} role="region" aria-label="Talk to advisor section">
      <div
        id="advisor-selection-status"
        className="visually-hidden"
        role="status"
        aria-live="polite"
      />

      {loadingAreas && <LoadingBackdrop text="Loading priorities..." />}

      {(() => {
        switch (step) {
          case 'talkToAdvisorMessage':
            smoothScrollTo(0, 0);

            return (
              <div ref={stepContentRef} tabIndex={-1} aria-labelledby="advisor-intro-title">
                <div className={styles.actionContainer}>
                  <div className={styles.cardContainer}>
                    {companyConfig.talkToAdvisor && (
                      <>
                        {companyConfig.talkToAdvisorMessage && (
                          <div id="advisor-intro-title">
                            <SanitizedText as="div" content={companyConfig.talkToAdvisorMessage} />
                          </div>
                        )}

                        <div className={styles.prioritiesCard}>
                          <MotionText
                            as="h4"
                            className={styles.sectionTitle}
                            text="Your current priorities"
                          />
                          <MotionText
                            as="text"
                            className={styles.sectionDescription}
                            text="These areas are used to create your personalised action plan."
                          />
                          <div className={styles.currentPrioritiesGrid}>
                            {activePriorities.length > 0 ? (
                              activePriorities.map((priority) => (
                                <CurrentPrioritiesCard
                                  key={priority.id}
                                  priority={priority}
                                  handleRemovePriority={() => undefined}
                                  removeEnabled={false}
                                />
                              ))
                            ) : (
                              <div className={styles.emptyPrioritiesContainer}>
                                <div className={styles.emptyContainer}>
                                  <MotionImage
                                    src={LightBulbIcon}
                                    alt=""
                                    aria-hidden="true"
                                    className={styles.lightBulbIcon}
                                  />
                                  <div className={styles.emptyPrioritiesText}>
                                    <h5>You haven&apos;t selected any priorities yet</h5>
                                    <p>
                                      Selecting priorities helps us create a personalised action
                                      plan focused on what matters most to you.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          className={styles.navigateBtnContainer}
                          role="group"
                          aria-label="Choose whether to discuss with an advisor"
                        >
                          <SystemButton
                            variant="outlinedPrimarySkyBlue"
                            onClick={handleSkip}
                            aria-label="Skip talking to an advisor"
                          >
                            No, skip this process
                          </SystemButton>
                          <SystemButton
                            variant="primarySkyBlue"
                            onClick={() => handleStep('talkToAdvisorDiscussion')}
                            aria-label="Yes, I'd like to discuss with an advisor"
                          >
                            Yes, I&apos;d like to discuss
                          </SystemButton>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );

          case 'talkToAdvisorDiscussion':
            return (
              <div
                ref={stepContentRef}
                tabIndex={-1}
                aria-labelledby="select-discussion-areas-title"
              >
                <div className={styles.actionContainer}>
                  <div className={styles.cardContainer}>
                    {companyConfig.talkToAdvisor && (
                      <>
                        {TALK_TO_ADVISOR_AREAS_DISCUSS_MESSAGE && (
                          <div id="select-discussion-areas-title">
                            <SanitizedText
                              as="div"
                              content={TALK_TO_ADVISOR_AREAS_DISCUSS_MESSAGE}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <div className={styles.coloredBox}>
                      <div id="selection-limit-hint" className="visually-hidden">
                        You can select up to 10 areas to discuss
                      </div>
                      <div className={styles.recommendedPriorityGrid}>
                        {focusAreas?.length > 0 &&
                          focusAreas?.map((priority) => (
                            <RecommendedAreasCard
                              key={priority.id}
                              priority={priority}
                              isSelected={selectedAreaIds.includes(priority.focusAreaId)}
                              handleRemovePriority={() =>
                                handleRemovePriority(priority.focusAreaId)
                              }
                              handleAddPriority={() => handleSelectFocus(priority.focusAreaId)}
                              alignment
                            />
                          ))}
                      </div>
                    </div>

                    <div className={styles.navigateBtnContainer}>
                      <SystemButton
                        variant="primarySkyBlue"
                        disabled={selectedAreaIds?.length < 1}
                        onClick={handleSubmitAdvisor}
                        aria-label={`Continue with ${selectedAreaIds.length} selected area${
                          selectedAreaIds.length !== 1 ? 's' : ''
                        }`}
                      >
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Continue
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </SystemButton>
                    </div>
                  </div>
                </div>
              </div>
            );

          case 'talkToAdvisorCompletion':
            smoothScrollTo(0, 0);

            return (
              <div ref={stepContentRef} tabIndex={-1} aria-labelledby="advisor-completion-title">
                <div className={styles.actionContainer}>
                  <div className={styles.cardContainer}>
                    {companyConfig.talkToAdvisor && companyConfig.talkToAdvisorCompleteMessage && (
                      <>
                        <div id="advisor-completion-title">
                          <SanitizedText
                            as="div"
                            content={companyConfig.talkToAdvisorCompleteMessage}
                          />
                        </div>

                        <div className={styles.centeredButton}>
                          <SystemButton
                            variant="primarySkyBlue"
                            onClick={handleSkip}
                            aria-label="Continue to next section"
                          >
                            {' '}
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Continue
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          </SystemButton>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );

          default:
            return null;
        }
      })()}
    </div>
  );
};

TalkToAdviserAreasSelection.defaultProps = {
  handleSkip: () => null,
};

interface TalkToAdviserAreasSelectionProps {
  handleSkip?: () => void;
}
